import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { createStore } from "solid-js/store";
import { Portal } from "solid-js/web";
import { TransitionGroup } from "@fleet/ui";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { closestSelector } from "@/lib/gestures/guards";
import { prefersReducedMotion } from "@/lib/gestures/motion";
import { createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { TOAST_SWIPE } from "@/lib/gestures/presets";

type ToastType = "success" | "error" | "info" | "warning";

interface ToastAction {
  /** Label of the action button (already localized), e.g. "Undo". */
  label: string;
  onClick: () => void;
}

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  action?: ToastAction;
  /**
   * Optional `data-testid` for the toast. Exists so an e2e test can anchor to
   * a SPECIFIC toast as a positive assertion: a generic `getByRole("alert")` matches
   * any notice and cannot distinguish "alerted the expected thing" from "alerted something
   * else" (P23/CC-60 · P25).
   */
  testId?: string;
}

const TOAST_DURATION = 5000;
const RADIX_36 = 36;
const ID_LENGTH = 9;
const [toasts, setToasts] = createStore<Toast[]>([]);

export function showToast(
  message: string,
  type: ToastType = "info",
  options?: { action?: ToastAction; durationMs?: number; testId?: string },
) {
  const id = Math.random().toString(RADIX_36).slice(2, ID_LENGTH);
  setToasts([...toasts, { id, message, type, action: options?.action, testId: options?.testId }]);

  setTimeout(() => {
    setToasts(toasts.filter((t) => t.id !== id));
  }, options?.durationMs ?? TOAST_DURATION);
}

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  hapticMs: HAPTIC_VIBRATION_MS,
  commitAnimationMs: COMMIT_ANIMATION_MS,
  cancelAnimationMs: CANCEL_ANIMATION_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
} = TOAST_SWIPE;

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

/**
 * A single toast row with swipe-to-dismiss. Horizontal drag in either direction
 * dismisses past the commit threshold or on a fling; short releases snap back.
 * Keeps the explicit close button and auto-dismiss timer as the source of
 * truth — drags that begin on the close button are ignored.
 */
function ToastItem(props: ToastItemProps) {
  const { t } = useI18n();
  const [tx, setTx] = createSignal(0);
  const [isSwiping, setIsSwiping] = createSignal(false);
  const [isAnimating, setIsAnimating] = createSignal(false);

  let surfaceRef: HTMLDivElement | undefined;
  // The toast id is stable for the lifetime of this row; capture it so timer
  // callbacks don't read props reactively outside a tracked scope. The
  // recognizer callbacks below are pointer-event handlers in all but name.
  // oxlint-disable-next-line solid/reactivity
  const toastId = props.toast.id;

  const settle = (delayMs: number, after: () => void) => {
    if (prefersReducedMotion()) {
      setTx(0);
      setIsAnimating(false);
      after();
      return;
    }
    setTimeout(after, delayMs);
  };

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    commitPx: COMMIT_THRESHOLD_PX,
    fling: TOAST_SWIPE.fling,
    haptics: true,
    hapticMs: HAPTIC_VIBRATION_MS,
    // Ignore drags that begin on the close button so the tap stays a tap.
    canStart: ({ target }) => !closestSelector(target, 'button, [data-no-swipe="true"]'),
    onStart: () => {
      setIsSwiping(true);
      setIsAnimating(false);
    },
    onMove: (sample) => {
      setTx(sample.dx);
    },
    onEnd: (sample) => {
      setIsSwiping(false);
      setIsAnimating(true);

      const width = surfaceRef?.clientWidth ?? FALLBACK_WIDTH_PX;
      const committed = sample.distance > COMMIT_THRESHOLD_PX || sample.isFling;

      if (committed) {
        const goingLeft = sample.dx < 0;
        setTx(goingLeft ? -width : width);
        // oxlint-disable-next-line solid/reactivity
        settle(COMMIT_ANIMATION_MS, () => {
          props.onDismiss(toastId);
        });
      } else {
        setTx(0);
        settle(CANCEL_ANIMATION_MS, () => {
          setIsAnimating(false);
        });
      }
    },
    onCancel: () => {
      setIsSwiping(false);
      setIsAnimating(true);
      setTx(0);
      settle(CANCEL_ANIMATION_MS, () => {
        setIsAnimating(false);
      });
    },
  });

  const handlers = pointerHandlers(recognizer);

  return (
    <div
      ref={surfaceRef}
      data-testid={props.toast.testId}
      role={props.toast.type === "error" ? "alert" : "status"}
      aria-live={props.toast.type === "error" ? "assertive" : "polite"}
      class="pointer-events-auto flex min-w-0 items-center justify-between gap-3 rounded-lg border bg-popover p-4 shadow-lg"
      classList={{
        "border-success/30 text-success": props.toast.type === "success",
        "border-destructive/30 text-destructive": props.toast.type === "error",
        "border-warning/30 text-warning": props.toast.type === "warning",
        "border-primary/30 text-primary": props.toast.type === "info",
      }}
      style={{
        // Inline styles ONLY during swipe gesture/animation: if applied
        // always applied, the inline `transform`/`transition` OVERRIDE the
        // .toast-exit-* classes of TransitionGroup and the exit `transitionend` never
        // fires → toasts never auto-dismissed.
        ...(isSwiping() || tx() !== 0
          ? {
              transform: `translateX(${tx()}px)`,
              transition: isSwiping() ? "none" : `transform ${CANCEL_ANIMATION_MS}ms ease-out`,
              opacity: isAnimating() && tx() !== 0 ? "0" : "1",
            }
          : {}),
        "touch-action": "pan-y",
      }}
      onPointerDown={handlers.onPointerDown}
      onPointerMove={handlers.onPointerMove}
      onPointerUp={handlers.onPointerUp}
      onPointerCancel={handlers.onPointerCancel}
    >
      <div class="flex min-w-0 items-center gap-3">
        <Show when={props.toast.type === "success"}>
          <CheckCircle2 class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "error"}>
          <AlertCircle class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "warning"}>
          <AlertTriangle class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "info"}>
          <Info class="h-5 w-5 shrink-0" />
        </Show>
        <span class="min-w-0 break-words text-sm font-medium">{props.toast.message}</span>
      </div>
      <Show when={props.toast.action}>
        {(action) => (
          <button
            type="button"
            data-no-swipe="true"
            onClick={() => {
              action().onClick();
              props.onDismiss(toastId);
            }}
            data-testid="toast-action"
            class="shrink-0 rounded-md border border-current/25 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide transition-colors hover:bg-black/5"
          >
            {action().label}
          </button>
        )}
      </Show>
      <button
        type="button"
        data-no-swipe="true"
        // A11y: the icon is `aria-hidden` (lucide default), so without a label
        // this is an unnamed button for AT users.
        aria-label={t(msg`Dismiss notification`)}
        onClick={() => {
          props.onDismiss(toastId);
        }}
        class="shrink-0 rounded-full p-1 transition-colors hover:bg-black/5"
      >
        <X class="h-4 w-4" />
      </button>
    </div>
  );
}

export function Toaster() {
  const removeToast = (id: string) => {
    setToasts(toasts.filter((t) => t.id !== id));
  };

  return (
    <Portal>
      <div
        class="pointer-events-none fixed z-[300] flex flex-col gap-2 left-[calc(0.75rem+var(--safe-area-inset-left))] sm:left-auto sm:w-full sm:max-w-[400px]"
        style={{
          right: "calc(0.75rem + var(--safe-area-inset-right))",
          bottom: "calc(0.75rem + var(--safe-area-inset-bottom))",
        }}
      >
        <TransitionGroup name="toast">
          <For each={toasts}>{(toast) => <ToastItem toast={toast} onDismiss={removeToast} />}</For>
        </TransitionGroup>
      </div>
    </Portal>
  );
}
